import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, PlayCircle, CalendarClock, Layers, Users, ShieldCheck,
  Workflow, Receipt, ClipboardCheck, BarChart3, Landmark, MessagesSquare,
  CalendarDays, Network, FileBadge, Headphones, CheckCircle2, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { VideoThumbnail, VideoLightbox } from '../components/HeroVideo';
import {
  heroStats, trustedLogos, whyFeatures, platformFeatures, audiences,
  localFeatures, testimonials, plans, resources, faqs, images,
} from '../mock';

const iconMap = {
  CalendarClock, Layers, Users, ShieldCheck, Workflow, Receipt,
  ClipboardCheck, BarChart3, Landmark, MessagesSquare, CalendarDays,
  Network, FileBadge, Headphones,
};

function Icon({ name, className }) {
  const Cmp = iconMap[name] || Sparkles;
  return <Cmp className={className} />;
}

export default function Home() {
  const [videoOpen, setVideoOpen] = React.useState(false);
  return (
    <div className="bg-[#fdfaf3]">
      <VideoLightbox open={videoOpen} onOpenChange={setVideoOpen}/>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#f4efe4] via-[#fdfaf3] to-[#fdfaf3]" />
        <div className="absolute top-0 right-0 -z-10 h-96 w-96 rounded-full bg-emerald-100 blur-3xl opacity-60" />
        <div className="absolute top-40 left-0 -z-10 h-80 w-80 rounded-full bg-sky-200 blur-3xl opacity-40" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-sky-50 text-sky-800 hover:bg-sky-50 border-sky-200 font-medium">
                <Sparkles size={12} className="mr-1"/> Powered by Saber Accounting
              </Badge>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold tracking-tight text-stone-900 leading-[1.05]">
                The smarter way to <span className="text-emerald-800">run, grow</span> and <span className="text-sky-700">scale</span> your firm.
              </h1>
              <p className="mt-6 text-lg text-stone-700 max-w-xl leading-relaxed">
                Accu Desk helps growing tax, accounting, bookkeeping and audit firms keep every job, deadline and team member on track — all from one place.
              </p>
              <p className="mt-4 text-stone-900 font-medium">Never miss another deadline. Know exactly what your team is working on.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/signup">
                  <Button size="lg" className="bg-emerald-800 hover:bg-emerald-900 text-white shadow-md h-12 px-6">
                    Start Free Trial <ArrowRight size={16} className="ml-2"/>
                  </Button>
                </Link>
                <Button size="lg" variant="outline" onClick={() => setVideoOpen(true)} data-testid="hero-watch-tour-btn" className="h-12 px-6 border-sky-300 text-sky-700 hover:bg-sky-50 hover:text-sky-800 hover:border-sky-400">
                  <PlayCircle size={18} className="mr-2"/> Watch the 40s tour
                </Button>
              </div>
              <p className="mt-4 text-sm text-stone-500">No credit card required · 30-day free trial · SA-based support</p>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-emerald-200/40 to-amber-200/30 blur-2xl" />
              <div className="relative"><VideoThumbnail onPlay={() => setVideoOpen(true)}/></div>
              <div className="absolute -bottom-6 -left-6 hidden md:block rounded-xl bg-white shadow-xl ring-1 ring-stone-200 p-4 w-56">
                <div className="flex items-center gap-2 text-emerald-800"><CheckCircle2 size={18}/> <span className="text-sm font-medium">VAT201 filed</span></div>
                <div className="mt-1 text-xs text-stone-500">Sandton Holdings · 2 mins ago</div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {heroStats.map((s, i) => (
              <div key={s.label} className="rounded-xl bg-white/60 backdrop-blur border border-stone-200 p-5">
                <div className={`text-3xl lg:text-4xl font-serif font-semibold ${i % 2 === 0 ? 'text-emerald-900' : 'text-sky-700'}`}>{s.value}</div>
                <div className="mt-1 text-sm font-medium text-stone-800">{s.label}</div>
                <div className="text-xs italic text-stone-500">{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Trust logos */}
          <div className="mt-16 text-center">
            <div className="text-xs tracking-widest text-stone-500 font-semibold">TRUSTED BY 2000+ SOUTH AFRICAN ACCOUNTANTS AND AUDITORS</div>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
              {trustedLogos.map((l) => (
                <div key={l} className="text-stone-400 hover:text-stone-700 transition-colors font-serif text-lg font-medium tracking-wide">{l}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-white py-20 lg:py-28 border-t border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-widest font-semibold text-emerald-800">WHY ACCU DESK</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-stone-900 max-w-4xl leading-tight">
            Switching between systems costs you thousands in lost revenue. Run your firm through one system.
          </h2>
          <div className="mt-6 grid lg:grid-cols-2 gap-8 max-w-5xl">
            <p className="text-stone-700 leading-relaxed">Jumping between systems to manage work is costing you valuable, billable hours. <span className="font-semibold text-stone-900">Accu Desk brings your jobs, deadlines, billing, clients and team together in one place</span>, so you can stay in control and keep your practice running smoothly.</p>
            <p className="text-stone-700 leading-relaxed">Most South African practices run on a deadline spreadsheet, a billing tool, an audit package and four open browser tabs. <span className="font-semibold text-stone-900">Accu Desk replaces all of these with one single platform</span> — purpose-built for the way SA accountants and auditors work.</p>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyFeatures.map((f) => (
              <div key={f.title} className="group rounded-xl border border-stone-200 p-6 hover:border-emerald-300 hover:shadow-lg transition-all bg-stone-50/50">
                <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-800 grid place-items-center group-hover:bg-emerald-800 group-hover:text-white transition-colors">
                  <Icon name={f.icon} className="h-5 w-5"/>
                </div>
                <div className="mt-4 font-semibold text-stone-900">{f.title}</div>
                <div className="mt-2 text-sm text-stone-600 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM */}
      <section id="platform" className="py-20 lg:py-28 bg-[#fdfaf3]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-widest font-semibold text-emerald-800">THE FULL PLATFORM</div>
          <div className="mt-3 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-stone-900 max-w-3xl leading-tight">See every client. Every deadline. Every team member. All in one place.</h2>
            <Link to="/signup"><Button className="bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-5">Start Free Trial <ArrowRight size={16} className="ml-2"/></Button></Link>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platformFeatures.map((f, i) => {
              const isBlue = i % 2 === 1;
              return (
              <Card key={f.title} className="border-stone-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden bg-white">
                <div className={`h-1 ${isBlue ? 'bg-gradient-to-r from-sky-600 to-sky-400' : 'bg-gradient-to-r from-emerald-700 to-emerald-500'}`}/>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className={`text-[11px] font-bold tracking-widest ${isBlue ? 'text-sky-700' : 'text-emerald-800'}`}>{f.tag}</div>
                    <div className={`h-9 w-9 rounded-lg grid place-items-center ${isBlue ? 'bg-sky-50 text-sky-700' : 'bg-emerald-50 text-emerald-800'}`}>
                      <Icon name={f.icon} className="h-4 w-4"/>
                    </div>
                  </div>
                  <div className="mt-4 font-serif text-xl font-semibold text-stone-900">{f.title}</div>
                  <div className="mt-2 text-sm text-stone-600 leading-relaxed">{f.body}</div>
                  <div className={`mt-4 text-sm font-medium cursor-pointer inline-flex items-center gap-1 ${isBlue ? 'text-sky-700 hover:text-sky-800' : 'text-emerald-800 hover:text-emerald-900'}`}>
                    Explore <ArrowRight size={14}/>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Deadline Story */}
      <section className="py-20 lg:py-28 bg-white border-t border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-4 rounded-3xl bg-emerald-100/60 blur-2xl"/>
            <img src={images.review} alt="Reviewing deadlines" className="relative rounded-2xl shadow-xl ring-1 ring-stone-900/5 w-full object-cover"/>
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-xs tracking-widest font-semibold text-emerald-800">DEADLINES &amp; COMPLIANCE</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-serif font-semibold text-stone-900 leading-tight">Never miss another deadline.</h2>
            <p className="mt-4 text-stone-700 leading-relaxed max-w-lg">The Accu Desk To-Do dashboard ensures your team knows exactly what to do and when. Receive timely reminders and give your team complete visibility, so nothing slips through the cracks.</p>
            <ul className="mt-6 space-y-3">
              {['Automated SARS &amp; CIPC due dates', 'Day-counters on every task', 'Escalation windows with reminder emails'].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-700 mt-0.5"/>
                  <span className="text-stone-700" dangerouslySetInnerHTML={{__html: t}}/>
                </li>
              ))}
            </ul>
            <Link to="/signup"><Button className="mt-8 bg-emerald-800 hover:bg-emerald-900 text-white">Learn more <ArrowRight size={16} className="ml-2"/></Button></Link>
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section id="audiences" className="py-20 lg:py-28 bg-[#fdfaf3]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-widest font-semibold text-emerald-800">WHO IT&apos;S FOR</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-stone-900 max-w-3xl leading-tight">Built for the way South African and Namibian firms work</h2>
          <p className="mt-4 text-stone-700 max-w-2xl">From sole practitioners to multi-partner firms, Accu Desk adapts to how your team works.</p>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {audiences.map((a, idx) => (
              <div key={a.title} className={`group rounded-2xl border border-stone-200 bg-white p-7 hover:border-emerald-300 hover:shadow-xl transition-all ${idx === 0 || idx === 4 ? 'lg:col-span-2 md:col-span-2' : ''}`}>
                <div className="font-serif text-2xl font-semibold text-stone-900">{a.title}</div>
                <p className="mt-3 text-stone-600 leading-relaxed">{a.body}</p>
                <div className="mt-5 text-sm font-medium text-emerald-800 group-hover:text-emerald-900 inline-flex items-center gap-1 cursor-pointer">Explore <ArrowRight size={14}/></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SA Touch */}
      <section className="py-20 lg:py-28 bg-stone-950 text-stone-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs tracking-widest font-semibold text-sky-400">THE SOUTH AFRICAN TOUCH</div>
            <div className="mt-2 text-2xl font-serif italic text-stone-400">we&apos;ve sat in the SARS queue</div>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-white leading-tight">Built in South Africa. Built for South Africa.</h2>
            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              {localFeatures.map((f, i) => {
                const isBlue = i % 2 === 1;
                return (
                <div key={f.title} className="rounded-xl border border-stone-800 p-5 bg-stone-900/50 hover:bg-stone-900 transition-colors">
                  <div className={`h-10 w-10 rounded-lg grid place-items-center ${isBlue ? 'bg-sky-900/40 text-sky-400' : 'bg-emerald-900/40 text-emerald-400'}`}>
                    <Icon name={f.icon} className="h-5 w-5"/>
                  </div>
                  <div className="mt-4 font-semibold text-white">{f.title}</div>
                  <div className="mt-1 text-sm text-stone-400">{f.body}</div>
                </div>
                );
              })}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 rounded-3xl bg-sky-500/10 blur-3xl"/>
            <div className="absolute -inset-6 rounded-3xl bg-emerald-500/10 blur-3xl"/>
            <img src={images.metrics} alt="South African compliance" className="relative rounded-2xl shadow-2xl w-full object-cover ring-1 ring-sky-900/50"/>
          </div>
        </div>
      </section>

      {/* Audit */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs tracking-widest font-semibold text-emerald-800">AUDIT &amp; ASSURANCE</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-serif font-semibold text-stone-900 leading-tight">Plan an audit. See staff overlap. Track progress.</h2>
            <p className="mt-4 text-stone-700 leading-relaxed max-w-lg">Getting the full picture of an audit is easy in Accu Desk. Plan your audit with the needed lead sheets and working papers. Customise deadlines and subtasks so trainees, managers and partners stay in the loop.</p>
            <p className="mt-3 text-stone-700 max-w-lg">Our reports make it easy to see where team members overlap and where bottlenecks might occur.</p>
            <Link to="/contact"><Button className="mt-8 bg-emerald-800 hover:bg-emerald-900 text-white">Talk to the team <ArrowRight size={16} className="ml-2"/></Button></Link>
          </div>
          <div className="relative">
            <img src={images.audit} alt="Audit management" className="rounded-2xl shadow-xl ring-1 ring-stone-900/5 w-full object-cover"/>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 bg-[#fdfaf3]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-widest font-semibold text-emerald-800">TESTIMONIALS</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-stone-900 max-w-3xl leading-tight">Trusted by South African accounting firms.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="rounded-2xl bg-white border border-stone-200 p-7 hover:shadow-xl transition-shadow">
                <div className="text-emerald-800 text-5xl leading-none font-serif">&ldquo;</div>
                <p className="mt-2 text-stone-800 leading-relaxed">{t.quote}</p>
                <div className="mt-6 pt-5 border-t border-stone-100">
                  <div className="font-semibold text-stone-900">{t.name}</div>
                  <div className="text-sm text-stone-500">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 lg:py-28 bg-white border-t border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-widest font-semibold text-emerald-800">PRICING</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-stone-900">Simple pricing. No surprises.</h2>
          <p className="mt-4 text-stone-700 max-w-2xl">Whether you&apos;re just getting started or growing your practice, there&apos;s an Accu Desk plan to help you stay on top of every deadline and every client.</p>
          <p className="mt-2 text-stone-900 font-medium">Start with a 30-day free trial. No credit card required.</p>

          <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {plans.map((p) => (
              <div key={p.name} className={`relative rounded-2xl border p-8 flex flex-col ${p.highlighted ? 'border-emerald-800 bg-emerald-50/40 shadow-xl md:-translate-y-3' : 'border-stone-200 bg-white'}`}>
                {p.badge && <div className="absolute -top-3 left-8 bg-emerald-800 text-white text-xs font-semibold px-3 py-1 rounded-full">{p.badge}</div>}
                <div className="text-sm font-semibold uppercase tracking-wider text-stone-800">{p.name}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-serif font-semibold text-stone-900">{p.price}</span>
                  <span className="text-sm text-stone-500">{p.period}</span>
                </div>
                {p.addOn && <div className="mt-1 text-sm text-emerald-800 font-medium">{p.addOn}</div>}
                <p className="mt-3 text-sm text-stone-600">{p.description}</p>
                <div className="mt-4 text-sm font-medium text-stone-900">{p.tagline}</div>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-stone-700">
                      <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5 flex-shrink-0"/> {f}
                    </li>
                  ))}
                </ul>
                <Link to={p.contact ? '/contact' : '/signup'} className="mt-7">
                  <Button className={`w-full h-11 ${p.highlighted ? 'bg-emerald-800 hover:bg-emerald-900 text-white' : 'bg-stone-900 hover:bg-stone-800 text-white'}`}>{p.cta}</Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources */}
      <section id="resources" className="py-20 lg:py-28 bg-[#fdfaf3]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-widest font-semibold text-emerald-800">RESOURCES</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-stone-900">Guides, templates &amp; SA accounting news</h2>
          <p className="mt-4 text-stone-700 max-w-2xl">Practical resources for running a modern South African practice, from the SARS calendar to ready-to-use templates.</p>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {resources.map((r) => (
              <div key={r.title} className="group rounded-2xl overflow-hidden bg-white border border-stone-200 hover:shadow-xl transition-shadow">
                <div className="h-40 bg-gradient-to-br from-emerald-800 to-emerald-950 relative overflow-hidden">
                  <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,white,transparent_50%)]"/>
                  <div className="absolute bottom-3 left-4 text-white/90 font-serif text-lg">{r.tag}</div>
                </div>
                <div className="p-6">
                  <div className="font-semibold text-stone-900 leading-snug">{r.title}</div>
                  <p className="mt-2 text-sm text-stone-600">{r.excerpt}</p>
                  <div className="mt-4 text-sm font-medium text-emerald-800 inline-flex items-center gap-1">Read more <ArrowRight size={14}/></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 lg:py-28 bg-white border-t border-stone-200">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-widest font-semibold text-emerald-800">FAQ</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-stone-900">Everything you need to know.</h2>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-stone-200">
                <AccordionTrigger className="text-left font-serif text-lg text-stone-900 hover:text-emerald-800">{f.q}</AccordionTrigger>
                <AccordionContent className="text-stone-700 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-950 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs tracking-widest font-semibold text-emerald-300">READY WHEN YOU ARE</div>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold leading-tight">Optimise your practice today.</h2>
            <p className="mt-4 text-emerald-50/90 max-w-lg leading-relaxed">Need help choosing the right plan? Start free, and our SA-based team will help you map Accu Desk to your practice.</p>
            <p className="mt-2 text-emerald-200/80 text-sm">No pressure. No credit card.</p>
            <ul className="mt-6 space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-300"/> Join over 2000 accounting professionals</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-300"/> 30-day free trial</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-300"/> No credit card required</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-300"/> SA-based support team</li>
            </ul>
            <Link to="/signup"><Button size="lg" className="mt-8 bg-white text-emerald-900 hover:bg-emerald-50 h-12 px-6 shadow-lg">Start my free trial <ArrowRight size={16} className="ml-2"/></Button></Link>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 rounded-3xl bg-emerald-300/10 blur-3xl"/>
            <img src={images.collab} alt="Team collaboration" className="relative rounded-2xl shadow-2xl w-full object-cover ring-1 ring-white/10"/>
          </div>
        </div>
      </section>
    </div>
  );
}
